import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, MessageCircle } from "lucide-react";

export default function CommentSection({ targetType, targetId, title = "Discussion" }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get(`/comments?target_type=${targetType}&target_id=${targetId}`)
      .then((r) => setComments(r.data.comments || []))
      .catch(() => {});
  };

  useEffect(load, [targetType, targetId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to comment"); return; }
    if (!content.trim()) return;
    setLoading(true);
    try {
      await api.post("/comments", { target_type: targetType, target_id: targetId, content });
      setContent("");
      load();
      toast.success("Comment posted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post");
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <section className="py-16 px-6 md:px-12 lg:px-16 bg-halo-cream border-t border-border" data-testid="comments-section">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <MessageCircle className="w-7 h-7 text-halo-red" strokeWidth={2.5} />
          <h2 className="font-sports uppercase tracking-tight text-3xl sm:text-4xl text-halo-navy">{title}</h2>
          <span className="ml-auto font-sports uppercase text-sm text-halo-navy/60">{comments.length} {comments.length === 1 ? "Reply" : "Replies"}</span>
        </div>

        <form onSubmit={submit} className="mb-10 border-2 border-halo-navy bg-white" data-testid="comment-form">
          <textarea
            data-testid="comment-input"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={user ? "Share your take..." : "Sign in above to join the conversation"}
            disabled={!user}
            rows={3}
            className="w-full p-4 font-body resize-none focus:outline-none disabled:bg-halo-cream/50"
            maxLength={2000}
          />
          <div className="border-t-2 border-halo-navy/20 p-3 flex items-center justify-between">
            <span className="text-xs font-sports uppercase tracking-widest text-halo-navy/60">
              {user ? `Posting as ${user.name}` : "Sign in to comment"}
            </span>
            <button
              type="submit" disabled={!user || loading || !content.trim()}
              data-testid="comment-submit-btn"
              className="bg-halo-red hover:bg-halo-redHover disabled:opacity-40 text-white font-sports uppercase tracking-wider px-5 py-2 text-xs"
            >
              Post
            </button>
          </div>
        </form>

        <ul className="space-y-6">
          {comments.length === 0 ? (
            <li className="text-center text-halo-navy/50 font-editorial italic">No comments yet. Be the first to speak.</li>
          ) : comments.map((c) => (
            <li key={c.id} data-testid={`comment-${c.id}`} className="border-l-4 border-halo-red pl-4 py-2">
              <div className="flex items-center justify-between">
                <div className="font-sports uppercase tracking-wider text-sm text-halo-navy">{c.user_name}</div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-sports uppercase tracking-widest text-halo-navy/50">
                    {new Date(c.created_at).toLocaleString()}
                  </span>
                  {user && user.id === c.user_id && (
                    <button onClick={() => remove(c.id)} data-testid={`delete-comment-${c.id}`} className="text-halo-navy/40 hover:text-halo-red">
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-2 font-body text-halo-navy/85 whitespace-pre-line">{c.content}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
